/// <amd-module name="Analytics/Common/Utilities/QueryUtilities" />
import { AnalyticsODataVersion } from "Analytics/Common/ODataQueryOptions";
import { DateSKParser } from "Analytics/Common/Utilities/DateSKParser";

export interface DateSamplingSeries {
    /** A set of dateSK's. Sorting does not matter.  */
    sampleDates: string[];

    /** Describes if the dates are a continuous series of daily samples.
     *  Query generation uses set comprehension for daily samples, and uses set enumeration otherwise. */
    isDailyInterval?: boolean;
}

/** Floating current version for OData Rest Apis */
export const latestODataVersion = AnalyticsODataVersion.v4Preview;

// Analogous to Tfs/Service/WebAccess/Widgets/Scripts/DataServices/QueryUtilities.ts
export class QueryUtilities {
    /**
     * Helper to expand an array into a series of or statements.
     * Ex. isInArray("Id", ["1", "2", "3"], false) outputs "Id eq 1 or Id eq 2 or Id eq 3".
     * @param propertyName is the property of the entity to check if it is in the array of values.
     * @param useQuotes indicates whether each value should be wrapped in single quotes in the returned result. Defaults to false.
     * @param values are the values that the property should be compared against.
     * @returns a string of clauses linked by 'or'.
     */
    public static isInArray(propertyName: string, values: string[], useQuotes: boolean = false): string {
        const mapping = (val: string): string => {
            return useQuotes ? `${propertyName} eq '${val}'` : `${propertyName} eq ${val}`;
        };

        const clauses = values.map(mapping);
        return clauses.join(" or ");
    }

    /**
     * Parse datetime that is coming from analytics
     * Note: datetime from analytics always comes in ISO format
     * @param ignoreTimeZone Used to strip timezone from datetime, for example for Iterations where it's not important
     */
    public static parseAnalyticsDateTime(dateString: string, ignoreTimeZone: boolean): Date {
        // Diag.Debug.assertParamIsString(dateString, "dateString");

        // regex from Date.ts, modified to allow timezone removal
        const isoRegex = /^((\d{4}|\+{1}\d{6})-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,7})?)(Z|[+-]\d{2}:\d{2})?$/;
        if (ignoreTimeZone) {
            // Remove time zone offset or Z from the end of string.
            // This will parse time in local time zone in browsers.
            // Note that PhantomJS does not follow this behavior.
            dateString = dateString.replace(isoRegex, "$1");
        }
        return new Date(Date.parse(dateString));
    }

    /**
     * Replaces all instances of single-quotes with two sequential single-quotes.
     * This is the syntax for escaping single-quotes in OData queries.
     * @param value is the string to escape
     */
    public static escapeString(value: string): string {
        return value.split("'").join("''"); // Replace all
    }

    /** Returns a dates clause using range comprehension or sampling based on dates. */
    public static generateDatesClause(propertyName: string, dateSeries: DateSamplingSeries): string {
        const dateSKs = dateSeries.sampleDates.map(date => DateSKParser.dateStringToDateSK(date));
        if (dateSeries.isDailyInterval) {
            const earliestDateSK = Math.min(...dateSKs);
            const lastDateSK = Math.max(...dateSKs);
            return `(${propertyName} ge ${earliestDateSK} and ${propertyName} le ${lastDateSK})`;
        } else {
            return QueryUtilities.isInArray(propertyName, dateSKs.map(dateSK => dateSK.toString()), false /* useQuotes */);
        }
    }
}
